import * as React from 'react'
import { ReactElement } from 'react'
import { TreeItemLayout } from '@fluentui/react-components/unstable'
import { PaymentRegular, TimerRegular, VehicleCarRegular } from '@fluentui/react-icons'
import { InvoiceListItemDto, InvoiceMutationDto, LineItemType } from '../../../../../config/finance/invoiceConfig'
import { DisbursementListItemDto, TimeListItemDto, TravelListItemDto } from '../../../../../config/registrationConfig'
import TimeLineItem from './TimeLineItem'
import DisbursementLineItem from './DisbursementLineItem'
import { css, getId, Stack } from '@fluentui/react'
import { makeStyles } from '@fluentui/react-components'
import TravelLineItem from './TravelLineItem'
import { useFormikContext } from 'formik'
import MoneyText from '../../../../Text/Currency/MoneyText'

import { LineItemEntities, LineItemEntity } from '../../../../../hooks/finance/invoice/useLineItemEntities'
import InvoiceLineItem from './InvoiceLineItem'
import { TreeItemLayoutProps } from '@fluentui/react-tree'
import { VatType } from '../../../../../config/i18nConfig'

export type LineItemProps<E extends LineItemEntity> = {
    lineItemType: LineItemType
    entity: E
    defaultVat: VatType
    showPrice: boolean
    getPrice: LineItemEntities['getPrice']
}

type Props<E extends LineItemEntity> = LineItemProps<E> & {
    isDraggable: boolean
    actions?: TreeItemLayoutProps['actions']
}

export const LineItemTypeIcons: Map<LineItemType, ReactElement> = new Map<LineItemType, ReactElement>([
    [LineItemType.Disbursement, <PaymentRegular key={getId()} />],
    [LineItemType.Time, <TimerRegular key={getId()} />],
    [LineItemType.Travel, <VehicleCarRegular key={getId()} />],
    [LineItemType.Invoice, <PaymentRegular key={getId()} />],
])

export const LineItemTypeLabels = new Map([
    [LineItemType.Time, 'invoice.edit.form.invoiceline.type.time'],
    [LineItemType.Travel, 'invoice.edit.form.invoiceline.type.travel'],
    [LineItemType.Disbursement, 'invoice.edit.form.invoiceline.type.disbursement'],
    [LineItemType.Invoice, 'invoice.edit.form.invoiceline.type.invoice'],
])

const useStyles = makeStyles({
    root: {
        // Prevent accidentally selecting inside the line item.
        userSelect: 'none',
    },
})

export default function LineItem(props: Props<any>): ReactElement {
    const styles = useStyles()
    const { values } = useFormikContext<InvoiceMutationDto>()
    return (
        <TreeItemLayout
            iconBefore={
                <Stack styles={{ root: { cursor: props.isDraggable && 'move' } }}>
                    {LineItemTypeIcons.get(props.lineItemType)}
                </Stack>
            }
            className={css(styles.root)}
            aside={
                props.showPrice && (
                    <MoneyText
                        amount={props.getPrice(props.entity, props.defaultVat).subtotal}
                        alpha={values.currency}
                    />
                )
            }
            actions={props.actions || undefined}
        >
            {(() => {
                if (!props.entity) {
                    return <></>
                }

                switch (props.lineItemType) {
                    case LineItemType.Time:
                        return <TimeLineItem {...(props as LineItemProps<TimeListItemDto>)} />
                    case LineItemType.Travel:
                        return <TravelLineItem {...(props as LineItemProps<TravelListItemDto>)} />
                    case LineItemType.Disbursement:
                        return <DisbursementLineItem {...(props as LineItemProps<DisbursementListItemDto>)} />
                    case LineItemType.Invoice:
                        return <InvoiceLineItem {...(props as LineItemProps<InvoiceListItemDto>)} />
                }
                return <></>
            })()}
        </TreeItemLayout>
    )
}
